import { useRef, useEffect, useState } from 'react';

/**
 * Scroll-reveal wrapper: fades and lifts its children into place the first time
 * they enter the viewport. Used to give the otherwise-static landing sections a
 * consistent entrance so scrolling between sections feels intentional.
 *
 * Sections that already run their own IntersectionObserver reveal are left
 * unwrapped to avoid double-animating.
 *
 * Respects `prefers-reduced-motion`: motion-sensitive users see content
 * immediately with no transform.
 */
const Reveal = ({ children, delay = 0, className = '' }) => {
    const ref = useRef(null);
    const [shown, setShown] = useState(false);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;

        // Honor reduced-motion: skip the animation entirely.
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            setShown(true);
            return;
        }

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setShown(true);
                    observer.disconnect();
                }
            },
            { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    return (
        <div
            ref={ref}
            style={{ transitionDelay: `${delay}ms` }}
            className={`transition-all duration-700 ease-out will-change-transform ${
                shown ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            } ${className}`}
        >
            {children}
        </div>
    );
};

export default Reveal;
